import { FormEvent, useState, useEffect } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { useChurch, useCampuses } from '../hooks/useChurches'
import { useEvent, useEventOccurrences } from '../hooks/usePotlucks'
import { useUpdateEvent, useDeleteEvent, usePublishEvent, useDraftEvent } from '../hooks/useChurchAdmin'
import { ApiError } from '../lib/api'
import { EVENT_TYPES, EventTypeKey } from '../components/ui/eventTypes'
import { EventTimePicker } from '../components/ui/DateTimePicker'
import { getDenominationConfig } from '@fellowship/shared'
import { AdminShell } from '../components/admin/AdminShell'
import { AdminPageHeader, AdminCard, AdmBtn, FormField, TextInput, AdminTextArea, Toggle } from '../components/admin/AdminAtoms'
import { AdmIcn } from '../components/admin/AdminIcons'
import type { EventType, Campus, Church } from '@fellowship/shared'
import LocationPicker from '../components/LocationPicker'
import { ImageUpload } from '../components/admin/ImageUpload'
import { CapabilityToggleRow } from '../components/event/CapabilityToggleRow'
import { DishGoalsFields } from '../components/event/DishGoalsFields'
import { OutdoorDetailsFields } from '../components/event/OutdoorDetailsFields'

const EVENT_TYPE_KEYS = Object.keys(EVENT_TYPES) as EventTypeKey[]

function isoToLocalDatetime(iso: string): string {
  const d = new Date(iso)
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}T${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`
}

function churchAddress(church: Church): string {
  return [church.addressLine1, church.city, church.state, church.zip].filter(Boolean).join(', ')
}

function resolveLocationAddress(id: string, church: Church, campuses: Campus[], custom: string): string {
  if (id === 'custom') return custom || churchAddress(church)
  const campus = campuses.find((c) => `campus-${c.id}` === id)
  if (campus) return [campus.addressLine1, campus.city, campus.state, campus.zip].filter(Boolean).join(', ') || churchAddress(church)
  return churchAddress(church)
}

function resolveLocationLat(id: string, church: Church, campuses: Campus[], custom: number | null): number {
  if (id === 'custom') return custom ?? church.locationLat
  const campus = campuses.find((c) => `campus-${c.id}` === id)
  return campus?.locationLat != null ? campus.locationLat : church.locationLat
}

function resolveLocationLng(id: string, church: Church, campuses: Campus[], custom: number | null): number {
  if (id === 'custom') return custom ?? church.locationLng
  const campus = campuses.find((c) => `campus-${c.id}` === id)
  return campus?.locationLng != null ? campus.locationLng : church.locationLng
}

export default function EditChurchEventPage() {
  const { slug, id } = useParams<{ slug: string; id: string }>()
  const navigate = useNavigate()
  const { data: churchData } = useChurch(slug)
  const { data: eventData, isLoading: eventLoading } = useEvent(id)
  const { data: occurrencesData } = useEventOccurrences(id)
  const { data: campusData } = useCampuses(churchData?.data?.id)
  const church = churchData?.data
  const campuses = campusData?.data ?? []
  const event = eventData?.data
  const firstOccurrence = occurrencesData?.data?.[0]
  const totalRsvps = (occurrencesData?.data ?? []).reduce((s, o) => s + (o.rsvpCount ?? 0), 0)

  const updateEvent = useUpdateEvent(id!)
  const deleteEvent = useDeleteEvent()
  const publishEvent = usePublishEvent(id)
  const draftEvent = useDraftEvent(id)
  const denomConfig = getDenominationConfig(church?.denomination)

  const [initialized, setInitialized] = useState(false)
  const [eventType, setEventType] = useState<EventType>('potluck')
  const [title, setTitle] = useState('')
  const [description, setDescription] = useState('')
  const [tags, setTags] = useState('')
  const [maxAttendees, setMaxAttendees] = useState('')
  const [startsAt, setStartsAt] = useState('')
  const [endsAt, setEndsAt] = useState('')
  const [mainGoal, setMainGoal] = useState(0)
  const [saladGoal, setSaladGoal] = useState(0)
  const [sideGoal, setSideGoal] = useState(0)
  const [dessertGoal, setDessertGoal] = useState(0)
  const [drinkGoal, setDrinkGoal] = useState(0)
  const [outdoorDifficulty, setOutdoorDifficulty] = useState<'easy' | 'moderate' | 'strenuous'>('easy')
  const [outdoorDistance, setOutdoorDistance] = useState('')
  const [outdoorElevation, setOutdoorElevation] = useState('')
  const [outdoorMeetupNote, setOutdoorMeetupNote] = useState('')
  const [studyTopic, setStudyTopic] = useState('')
  const [studyScripture, setStudyScripture] = useState('')
  const [attributes, setAttributes] = useState<Record<string, boolean | undefined>>({})
  const [audience, setAudience] = useState<'everyone' | 'denomination'>('everyone')
  const [rsvpRequiresApproval, setRsvpRequiresApproval] = useState(false)
  const [hideAddressUntilApproved, setHideAddressUntilApproved] = useState(false)
  const [selectedLocationId, setSelectedLocationId] = useState<string>('church')
  const [customAddress, setCustomAddress] = useState('')
  const [customLat, setCustomLat] = useState<number | null>(null)
  const [customLng, setCustomLng] = useState<number | null>(null)
  const [imageUrl, setImageUrl] = useState<string | null>(null)
  const [errors, setErrors] = useState<Record<string, string>>({})
  const [confirmDelete, setConfirmDelete] = useState(false)
  const [confirmDraft, setConfirmDraft] = useState(false)

  // Pre-populate from existing event
  useEffect(() => {
    if (!event || !firstOccurrence || initialized) return
    setEventType(event.eventType ?? 'potluck')
    setTitle(event.title)
    setDescription(event.description ?? '')
    setTags(event.tags.join(', '))
    setMaxAttendees(event.maxAttendees ? String(event.maxAttendees) : '')
    setStartsAt(isoToLocalDatetime(firstOccurrence.startsAt))
    if (firstOccurrence.endsAt) setEndsAt(isoToLocalDatetime(firstOccurrence.endsAt))
    setAudience(event.audience ?? 'everyone')
    setRsvpRequiresApproval(event.rsvpRequiresApproval)
    setHideAddressUntilApproved(event.hideAddressUntilApproved)
    setImageUrl(event.imageUrl ?? null)
    setAttributes({ ...(event.attributes as Record<string, boolean | undefined>) })

    const meta = event.metadata as Record<string, unknown> | null ?? {}
    if (meta.dishGoals) {
      const g = meta.dishGoals as Record<string, number>
      setMainGoal(g.main ?? 0)
      setSaladGoal(g.salad ?? 0)
      setSideGoal(g.side ?? 0)
      setDessertGoal(g.dessert ?? 0)
      setDrinkGoal(g.drink ?? 0)
    }
    if (meta.difficulty) setOutdoorDifficulty(meta.difficulty as 'easy' | 'moderate' | 'strenuous')
    if (meta.distanceMiles) setOutdoorDistance(String(meta.distanceMiles))
    if (meta.elevationFt) setOutdoorElevation(String(meta.elevationFt))
    if (meta.meetupNote) setOutdoorMeetupNote(meta.meetupNote as string)
    if (meta.topic) setStudyTopic(meta.topic as string)
    if (meta.scriptureRef) setStudyScripture(meta.scriptureRef as string)

    // Try to match address to church/campus, otherwise fall back to custom
    setCustomAddress(event.addressText)
    setCustomLat(event.locationLat)
    setCustomLng(event.locationLng)
    setSelectedLocationId('custom')
    setInitialized(true)
  }, [event, firstOccurrence, initialized])

  // Auto-resolve location to 'church' if it matches after campuses load
  useEffect(() => {
    if (!initialized || !church || campuses.length === 0) return
    const churchAddr = churchAddress(church)
    if (customAddress === churchAddr) {
      setSelectedLocationId('church')
      return
    }
    for (const campus of campuses) {
      const campusAddr = [campus.addressLine1, campus.city, campus.state, campus.zip].filter(Boolean).join(', ')
      if (customAddress === campusAddr) {
        setSelectedLocationId(`campus-${campus.id}`)
        return
      }
    }
  }, [initialized, church, campuses, customAddress])

  useEffect(() => {
    if (selectedLocationId !== 'custom' || !church) return
    if (customAddress === '') setCustomAddress(churchAddress(church))
    if (customLat === null) setCustomLat(church.locationLat)
    if (customLng === null) setCustomLng(church.locationLng)
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedLocationId, church])

  function validate(): boolean {
    const errs: Record<string, string> = {}
    if (!title.trim()) errs.title = 'Title is required'
    if (!startsAt) errs.startsAt = 'Start date and time is required'
    setErrors(errs)
    return Object.keys(errs).length === 0
  }

  function handleTypeChange(key: EventType) {
    setEventType(key)
    setAttributes((prev) => ({
      ...prev,
      bringADish: key === 'potluck' ? true : undefined,
      outdoorActivity: key === 'outdoors' ? true : undefined,
    }))
  }

  function buildMetadata() {
    const meta: Record<string, unknown> = {}
    if (attributes.bringADish) {
      const dishGoals: Record<string, number> = {}
      if (mainGoal > 0) dishGoals.main = mainGoal
      if (saladGoal > 0) dishGoals.salad = saladGoal
      if (dessertGoal > 0) dishGoals.dessert = dessertGoal
      if (drinkGoal > 0) dishGoals.drink = drinkGoal
      if (sideGoal > 0) dishGoals.side = sideGoal
      if (Object.keys(dishGoals).length > 0) meta.dishGoals = dishGoals
    }
    if (attributes.outdoorActivity) {
      meta.difficulty = outdoorDifficulty
      if (outdoorDistance) meta.distanceMiles = parseFloat(outdoorDistance)
      if (outdoorElevation) meta.elevationFt = parseInt(outdoorElevation)
      if (outdoorMeetupNote.trim()) meta.meetupNote = outdoorMeetupNote.trim()
    }
    if (eventType === 'study') {
      if (studyTopic.trim()) meta.topic = studyTopic.trim()
      if (studyScripture.trim()) meta.scriptureRef = studyScripture.trim()
    }
    return Object.keys(meta).length > 0 ? meta : undefined
  }

  function buildPayload() {
    if (!church) return null
    const parsedTags = tags.split(',').map((t) => t.trim()).filter(Boolean)
    return {
      title: title.trim(),
      description: description.trim() || undefined,
      eventType,
      addressText: resolveLocationAddress(selectedLocationId, church, campuses, customAddress),
      locationLat: resolveLocationLat(selectedLocationId, church, campuses, customLat),
      locationLng: resolveLocationLng(selectedLocationId, church, campuses, customLng),
      audience,
      rsvpRequiresApproval,
      hideAddressUntilApproved: rsvpRequiresApproval && hideAddressUntilApproved,
      maxAttendees: maxAttendees ? parseInt(maxAttendees) : undefined,
      tags: parsedTags,
      imageUrl: imageUrl ?? undefined,
      metadata: buildMetadata(),
      attributes: { ...attributes },
      startsAt: new Date(startsAt).toISOString(),
      endsAt: endsAt ? new Date(endsAt).toISOString() : undefined,
    }
  }

  function handleSubmit(e: FormEvent) {
    e.preventDefault()
    if (!validate()) return
    const payload = buildPayload()
    if (!payload) return
    updateEvent.mutate(payload, {
      onSuccess: () => navigate(`/churches/${slug}/dashboard`),
    })
  }

  function handlePreview() {
    if (!validate()) return
    const payload = buildPayload()
    if (!payload) return
    updateEvent.mutate(payload, {
      onSuccess: () => navigate(`/events/${id}`),
    })
  }

  const isDraft = event?.status === 'draft'
  const serverError = updateEvent.error instanceof ApiError ? updateEvent.error.message : null

  if (eventLoading) {
    return (
      <AdminShell>
        <div className="flex items-center justify-center" style={{ height: '60vh' }}>
          <div className="text-[13px] text-muted">Loading…</div>
        </div>
      </AdminShell>
    )
  }

  return (
    <AdminShell>
      <AdminPageHeader
        breadcrumb={['Events', event?.title ?? 'Edit event']}
        eyebrow="Edit"
        title={event?.title ?? 'Edit event'}
        sub={isDraft ? 'This event is saved as a draft — not yet visible to others.' : 'Changes go live immediately.'}
      />

      {isDraft && (
        <div className="mb-5 px-4 py-3 rounded-xl bg-s2 border border-line-soft text-[13px] text-ink-2">
          📝 Draft — not yet published. Save and publish when ready.
        </div>
      )}

      <form onSubmit={handleSubmit} noValidate>
        {serverError && (
          <div role="alert" className="px-4 py-3 rounded-xl mb-5 bg-danger-soft border border-danger-line text-danger-deep text-[13px]">
            {serverError}
          </div>
        )}

        {/* Type picker */}
        <AdminCard title="Event type" style={{ marginBottom: 20 }}>
          <div className="grid grid-cols-6 gap-2.5">
            {EVENT_TYPE_KEYS.map((key) => {
              const t = EVENT_TYPES[key]
              const selected = eventType === key
              return (
                <button
                  key={key}
                  type="button"
                  onClick={() => handleTypeChange(key as EventType)}
                  aria-pressed={selected}
                  aria-label={t.label}
                  className="p-4 rounded-[14px] cursor-pointer text-left flex flex-col gap-2 font-sans border-[1.5px]"
                  style={{
                    background: selected ? t.soft : 'var(--surface-2)',
                    borderColor: selected ? t.color : 'var(--line-soft)',
                  }}
                >
                  <div
                    className="w-9 h-9 rounded-[10px] inline-flex items-center justify-center text-[18px]"
                    style={{ background: selected ? t.color : t.soft, color: selected ? '#fff' : t.color }}
                  >{t.glyph}</div>
                  <div className="text-[13px] font-semibold">{t.label}</div>
                  <div className="text-[11px] text-muted leading-[1.35]">{t.sub}</div>
                </button>
              )
            })}
          </div>
        </AdminCard>

        <div className="grid gap-5" style={{ gridTemplateColumns: '1.5fr 1fr' }}>

          {/* Left: main form */}
          <div className="flex flex-col gap-5">

            <AdminCard title="Basics">
              <div className="flex flex-col gap-4">
                <FormField label="Title" required htmlFor="title" error={errors.title}>
                  <TextInput
                    id="title"
                    type="text"
                    value={title}
                    onChange={(e) => setTitle(e.target.value)}
                    placeholder="Sabbath fellowship lunch"
                    aria-invalid={Boolean(errors.title)}
                  />
                </FormField>
                <FormField label="Description" hint="What the gathering is, who should come, what to expect.">
                  <AdminTextArea
                    rows={4}
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                    placeholder="Long tables, warm haystacks, and dessert that lingers…"
                  />
                </FormField>
                <FormField label="Tags" hint="Comma-separated · up to 5.">
                  <TextInput
                    type="text"
                    value={tags}
                    onChange={(e) => setTags(e.target.value)}
                    placeholder="vegetarian, gluten-free"
                  />
                  {denomConfig.suggestedTags.length > 0 && (
                    <div className="flex flex-wrap gap-1.5 mt-1">
                      {denomConfig.suggestedTags.map((tag) => (
                        <button
                          key={tag}
                          type="button"
                          onClick={() => {
                            const current = tags.split(',').map((t) => t.trim()).filter(Boolean)
                            if (!current.includes(tag)) setTags([...current, tag].join(', '))
                          }}
                          className="h-[26px] px-2.5 rounded-full bg-primary-soft text-primary-deep border-0 cursor-pointer text-[11.5px] font-medium font-sans"
                        >
                          {tag}
                        </button>
                      ))}
                    </div>
                  )}
                </FormField>
              </div>
            </AdminCard>

            <AdminCard title="When" style={{ overflow: 'visible' }}>
              <EventTimePicker
                startsAt={startsAt}
                endsAt={endsAt}
                onStartsAtChange={setStartsAt}
                onEndsAtChange={setEndsAt}
                startsAtError={errors.startsAt}
              />
            </AdminCard>

            <AdminCard title="Where">
              <div className="flex flex-col gap-3.5">
                <div className="flex flex-col gap-2">
                  {[
                    { id: 'church', label: church?.name ?? 'Church address', sub: church ? churchAddress(church) : '' },
                    ...campuses.filter(c => c.type !== 'main').map(c => ({
                      id: `campus-${c.id}`,
                      label: c.name,
                      sub: [c.addressLine1, c.city, c.state].filter(Boolean).join(', '),
                    })),
                    { id: 'custom', label: 'Custom location', sub: 'Enter a different address' },
                  ].map((opt) => (
                    <label
                      key={opt.id}
                      className={`flex items-center gap-3 px-3.5 py-2.5 rounded-xl cursor-pointer border-[1.5px] transition-colors ${
                        selectedLocationId === opt.id
                          ? 'bg-primary-soft border-primary'
                          : 'bg-s2 border-line-soft hover:border-line'
                      }`}
                    >
                      <input
                        type="radio"
                        name="location"
                        value={opt.id}
                        checked={selectedLocationId === opt.id}
                        onChange={() => setSelectedLocationId(opt.id)}
                        className="accent-primary shrink-0"
                      />
                      <div>
                        <div className={`text-[13px] font-semibold ${selectedLocationId === opt.id ? 'text-primary-deep' : 'text-ink'}`}>{opt.label}</div>
                        {opt.sub && <div className="text-[11.5px] text-muted">{opt.sub}</div>}
                      </div>
                    </label>
                  ))}
                </div>
                {selectedLocationId === 'custom' && (
                  <LocationPicker
                    addressText={customAddress}
                    onAddressChange={setCustomAddress}
                    lat={customLat}
                    lng={customLng}
                    onCoordinatesChange={(lat, lng) => { setCustomLat(lat); setCustomLng(lng) }}
                    addressError={errors.customAddress}
                    placeholder="Enter venue address"
                  />
                )}
              </div>
            </AdminCard>

            <CapabilityToggleRow
              bringADish={Boolean(attributes.bringADish)}
              outdoorActivity={Boolean(attributes.outdoorActivity)}
              onToggleDish={() => setAttributes((p) => ({ ...p, bringADish: p.bringADish ? undefined : true }))}
              onToggleOutdoor={() => setAttributes((p) => ({ ...p, outdoorActivity: p.outdoorActivity ? undefined : true }))}
            />

            {attributes.bringADish && (
              <AdminCard title="Dish goals">
                <DishGoalsFields
                  mainGoal={mainGoal} setMainGoal={setMainGoal}
                  saladGoal={saladGoal} setSaladGoal={setSaladGoal}
                  sideGoal={sideGoal} setSideGoal={setSideGoal}
                  dessertGoal={dessertGoal} setDessertGoal={setDessertGoal}
                  drinkGoal={drinkGoal} setDrinkGoal={setDrinkGoal}
                />
              </AdminCard>
            )}

            {attributes.outdoorActivity && (
              <AdminCard title="Outdoor details">
                <OutdoorDetailsFields
                  difficulty={outdoorDifficulty} setDifficulty={setOutdoorDifficulty}
                  distance={outdoorDistance} setDistance={setOutdoorDistance}
                  elevation={outdoorElevation} setElevation={setOutdoorElevation}
                  meetupNote={outdoorMeetupNote} setMeetupNote={setOutdoorMeetupNote}
                />
              </AdminCard>
            )}

            {eventType === 'study' && (
              <AdminCard>
                <div className="eyebrow mb-3">Study details</div>
                <div className="flex flex-col gap-3.5">
                  <FormField label="Topic" htmlFor="studyTopic">
                    <input id="studyTopic" type="text" value={studyTopic} onChange={(e) => setStudyTopic(e.target.value)} placeholder="The Sermon on the Mount" className="input" />
                  </FormField>
                  <FormField label="Scripture reference" htmlFor="studyScripture">
                    <input id="studyScripture" type="text" value={studyScripture} onChange={(e) => setStudyScripture(e.target.value)} placeholder="Matthew 5–7" className="input" />
                  </FormField>
                </div>
              </AdminCard>
            )}
          </div>

          {/* Right: settings rail */}
          <div className="flex flex-col gap-5">

            <AdminCard title="Visibility & RSVPs">
              <div className="flex flex-col gap-4">
                {church?.denomination && church.denomination !== 'other' && (
                  <div>
                    <div className="text-[12px] font-semibold text-ink-2 mb-2">Who can see this event?</div>
                    <div className="flex gap-2">
                      {([
                        { value: 'everyone', label: 'Everyone', sub: 'Visible to all app users' },
                        { value: 'denomination', label: `${church.denomination.replace(/-/g, ' ')} only`, sub: 'Only users who share your denomination' },
                      ] as const).map(({ value, label, sub }) => (
                        <button
                          key={value}
                          type="button"
                          onClick={() => setAudience(value)}
                          style={{
                            flex: 1, padding: '8px 10px', borderRadius: 10, textAlign: 'left',
                            border: `${audience === value ? 2 : 1}px solid ${audience === value ? 'var(--primary)' : 'var(--line-soft)'}`,
                            background: audience === value ? 'var(--primary-soft)' : 'var(--surface)',
                            cursor: 'pointer', fontFamily: 'inherit',
                          }}
                        >
                          <div className="text-[12.5px] font-semibold text-ink capitalize">{label}</div>
                          <div className="text-[11px] text-muted mt-0.5">{sub}</div>
                        </button>
                      ))}
                    </div>
                  </div>
                )}
                <FormField label="Max guests" hint="Leave blank for no limit." htmlFor="maxAttendees">
                  <TextInput
                    id="maxAttendees"
                    type="number"
                    min={1}
                    value={maxAttendees}
                    onChange={(e) => setMaxAttendees(e.target.value)}
                    placeholder="No limit"
                    suffix="people"
                  />
                </FormField>
                <Toggle
                  on={rsvpRequiresApproval}
                  onChange={(v) => { setRsvpRequiresApproval(v); if (!v) setHideAddressUntilApproved(false) }}
                  label="Require RSVP approval"
                  sub="Coordinators manually approve each RSVP before attendees are confirmed."
                />
                {rsvpRequiresApproval && (
                  <Toggle
                    on={hideAddressUntilApproved}
                    onChange={setHideAddressUntilApproved}
                    label="Hide address until approved"
                    sub="Address only revealed after you approve each RSVP."
                  />
                )}
              </div>
            </AdminCard>

            <AdminCard title="Photo">
              {imageUrl ? (
                <div className="flex flex-col gap-2.5">
                  <div className="rounded-xl overflow-hidden" style={{ height: 120 }}>
                    <img src={imageUrl} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} />
                  </div>
                  <div className="flex gap-4 items-center">
                    <ImageUpload context="event-image" onUpload={setImageUrl}>
                      <span className="text-[13px] text-primary font-medium cursor-pointer font-sans">Change photo</span>
                    </ImageUpload>
                    <button type="button" onClick={() => setImageUrl(null)} style={{ fontSize: 12, color: 'var(--muted)', background: 'none', border: 'none', cursor: 'pointer', padding: 0 }} className="font-sans">Remove</button>
                  </div>
                </div>
              ) : (
                <ImageUpload context="event-image" onUpload={setImageUrl}>
                  <div style={{ height: 90, border: '1.5px dashed var(--line-soft)', borderRadius: 12, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 4, cursor: 'pointer' }}>
                    <AdmIcn name="image" size={20} />
                    <div className="text-[12.5px] font-semibold text-ink-2">Add a photo</div>
                    <div className="text-[11px] text-muted">JPEG, PNG, or WebP · 5 MB max</div>
                  </div>
                </ImageUpload>
              )}
            </AdminCard>

          </div>
        </div>

        {/* Save / action bar */}
        <div
          className="mt-7 px-[18px] py-3.5 bg-surface border border-line-soft rounded-2xl flex items-center gap-3"
          style={{ boxShadow: '0 10px 28px -16px rgba(60,40,10,0.25)' }}
        >
          {confirmDelete ? (
            <>
              <div className="flex-1 min-w-0">
                <div className="text-[13px] font-semibold">Delete this event?</div>
                <div className="text-[11.5px] text-muted">Removes the event and all occurrences. Existing RSVPs will be cancelled.</div>
              </div>
              <AdmBtn
                type="button"
                variant="danger"
                disabled={deleteEvent.isPending}
                onClick={() => id && deleteEvent.mutate(id, {
                  onSuccess: () => navigate(`/churches/${slug}/dashboard`),
                })}
              >
                {deleteEvent.isPending ? 'Deleting…' : 'Yes, delete'}
              </AdmBtn>
              <AdmBtn type="button" variant="ghost" onClick={() => setConfirmDelete(false)}>Cancel</AdmBtn>
            </>
          ) : confirmDraft ? (
            <>
              <div className="flex-1 min-w-0">
                <div className="text-[13px] font-semibold">Move to draft?</div>
                <div className="text-[11.5px] text-muted">
                  Hidden from your profile until republished.
                  {totalRsvps > 0 && <> <span className="text-warn">{totalRsvps} RSVP{totalRsvps !== 1 ? 's' : ''} won't be notified.</span></>}
                  {draftEvent.isError && <> <span className="text-danger">Failed — try again.</span></>}
                </div>
              </div>
              <AdmBtn
                type="button"
                variant="soft"
                disabled={draftEvent.isPending}
                onClick={() => draftEvent.mutate(undefined, {
                  onSuccess: () => navigate(`/churches/${slug}/events`),
                })}
              >
                {draftEvent.isPending ? 'Saving…' : 'Yes, move to draft'}
              </AdmBtn>
              <AdmBtn type="button" variant="ghost" onClick={() => setConfirmDraft(false)}>Cancel</AdmBtn>
            </>
          ) : (
            <>
              <AdmBtn type="button" variant="danger" onClick={() => setConfirmDelete(true)}>
                Delete
              </AdmBtn>
              {!isDraft && (
                <AdmBtn type="button" variant="soft" onClick={() => setConfirmDraft(true)}>
                  Move to draft
                </AdmBtn>
              )}
              <div className="flex-1" />
              <AdmBtn
                type="button"
                variant="ghost"
                disabled={updateEvent.isPending}
                onClick={handlePreview}
              >
                Preview
              </AdmBtn>
              <AdmBtn type="button" variant="ghost" onClick={() => navigate(`/churches/${slug}/dashboard`)}>
                Cancel
              </AdmBtn>
              <AdmBtn
                type="submit"
                variant="primary"
                size="lg"
                disabled={updateEvent.isPending}
              >
                {updateEvent.isPending ? 'Saving…' : 'Save changes'}
              </AdmBtn>
              {isDraft && (
                <AdmBtn
                  type="button"
                  variant="primary"
                  size="lg"
                  disabled={publishEvent.isPending || updateEvent.isPending}
                  onClick={() => {
                    if (!validate()) return
                    const payload = buildPayload()
                    if (!payload) return
                    updateEvent.mutate(payload, {
                      onSuccess: () => publishEvent.mutate(undefined, {
                        onSuccess: () => navigate(`/churches/${slug}/dashboard`),
                      }),
                    })
                  }}
                >
                  {publishEvent.isPending ? 'Publishing…' : 'Save & publish'}
                </AdmBtn>
              )}
            </>
          )}
        </div>
      </form>

    </AdminShell>
  )
}
